// components/MemberPortal/MemberPortalRouter.js
import React, { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../../axiosConfig';

// Import components
import MemberPortalSetup from './Setup';
import VerificationMethodSelection from './VerificationMethodSelection';
import VerificationPending from './VerificationPending';
import VerificationRejected from './VerificationRejected';
import MemberPortalDashboard from './Dashboard';

const MemberPortalRouter = () => {
  const [memberProfile, setMemberProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkMemberStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/member-portal/my-profile/');
      setMemberProfile(res.data || null);
    } catch (error) {
      if (error.response?.status === 404) {
        // No profile yet (expected first-time state)
        setMemberProfile(null);
      } else {
        console.error('Unexpected error loading member profile:', error);
        setMemberProfile(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔑 actually run the check
  useEffect(() => {
    checkMemberStatus();
  }, [checkMemberStatus]);

  // Let children trigger a refresh after actions (e.g., created profile / resubmitted docs)
  const refresh = () => checkMemberStatus();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner2"></div>
        <p>Loading Member Portal...</p>
      </div>
    );
  }

  // No profile yet → go to setup
  if (!memberProfile) {
    return <MemberPortalSetup onProfileCreated={refresh} />;
  }

  // Route by verification status (defensive default to 'pending' UX)
  const status = memberProfile.verification_status || 'pending';

  if (status === 'approved') {
    return <MemberPortalDashboard memberProfile={memberProfile} />;
  }
  if (status === 'rejected') {
    return <VerificationRejected memberProfile={memberProfile} onRetry={refresh} />;
  }

  // pending status: check if user has actually started verification
  if (status === 'pending') {
    // If verification_method is set, show pending screen
    if (memberProfile.verification_method) {
      return <VerificationPending memberProfile={memberProfile} onRefresh={refresh} />;
    }
    // If no verification_method, user hasn't started - show method selection
    return <VerificationMethodSelection onMethodChosen={refresh} />;
  }

  // unknown status fallback
  return <VerificationPending memberProfile={memberProfile} onRefresh={refresh} />;
};

export default MemberPortalRouter;
